import * as React from "react";
import { Loader } from "lucide-react";
import { Request, RequestCategory } from "@/types/research.types";
import { useRequestGallery } from "./RequestGalleryContext";
import RequestItem from "./RequestItem";
import {
  CategoryRow,
  StaticCategoryRow,
  ConfirmDeleteDialog,
  getCategoriesToShow,
  useDeleteHandlers,
} from "./RequestCategoryList.utils";

const RequestList = React.lazy(() => import("./RequestList"));

interface RequestCategoryListProps {
  onRequestSelect: (content: string, request?: Request) => void;
}

const DeleteDialogs: React.FC<{ deleteHandlers: ReturnType<typeof useDeleteHandlers> }> = ({
  deleteHandlers,
}) => (
  <>
    <ConfirmDeleteDialog
      isOpen={deleteHandlers.isDeleteCategoryDialogOpen}
      onOpenChange={deleteHandlers.setIsDeleteCategoryDialogOpen}
      title="Delete Category"
      description="Are you sure you want to delete this category? All requests within this category will also be deleted."
      onConfirm={deleteHandlers.confirmDeleteCategory}
    />
    <ConfirmDeleteDialog
      isOpen={deleteHandlers.isDeleteRequestDialogOpen}
      onOpenChange={deleteHandlers.setIsDeleteRequestDialogOpen}
      title="Delete Request"
      description="Are you sure you want to delete this request? This action cannot be undone."
      onConfirm={deleteHandlers.confirmDeleteRequest}
    />
  </>
);

const CategoryListContent: React.FC<{
  onRequestSelect: (content: string, request?: Request) => void;
  categoriesToShow: RequestCategory[];
  onCategoryClick: (category: RequestCategory) => void;
  handleEditRequest: (e: React.MouseEvent, request: Request) => void;
  deleteHandlers: ReturnType<typeof useDeleteHandlers>;
}> = ({
  onRequestSelect,
  categoriesToShow,
  onCategoryClick,
  handleEditRequest,
  deleteHandlers,
}) => {
  const {
    searchQuery,
    handleCategoryClick,
    currentParentId,
    handleEditCategory,
    filteredRequests,
  } = useRequestGallery();

  return (
    <>
      {searchQuery &&
        categoriesToShow.length === 0 &&
        filteredRequests.length === 0 &&
        currentParentId == null && (
          <div className="flex flex-col items-center justify-center h-full p-6 text-center text-neutral-500 text-sm">
            <p>No results found</p>
          </div>
        )}
      {!searchQuery && currentParentId == null && (
        <>
          <StaticCategoryRow label="ALL" onClick={() => handleCategoryClick("all")} />
          <StaticCategoryRow label="DEFAULT" onClick={() => handleCategoryClick("default")} />
        </>
      )}
      {categoriesToShow.map((category) => (
        <CategoryRow
          key={category._id}
          category={category}
          onClick={() => onCategoryClick(category)}
          onEdit={handleEditCategory}
          onDelete={deleteHandlers.handleDeleteCategory}
        />
      ))}
      {searchQuery &&
        filteredRequests.length > 0 &&
        filteredRequests.map((request) => (
          <RequestItem
            key={request.id}
            request={request}
            onClick={onRequestSelect}
            onEdit={handleEditRequest}
            onDelete={deleteHandlers.handleDeleteRequest}
          />
        ))}
      {currentParentId != null && (
        <React.Suspense
          fallback={
            <div className="flex flex-col items-center justify-center p-4">
              <Loader className="animate-spin" size={24} />
            </div>
          }
        >
          <RequestList onRequestSelect={onRequestSelect} />
        </React.Suspense>
      )}
      <DeleteDialogs deleteHandlers={deleteHandlers} />
    </>
  );
};

const RequestCategoryList: React.FC<RequestCategoryListProps> = ({ onRequestSelect }) => {
  const {
    categories,
    isLoadingCategories,
    searchQuery,
    handleCategoryClick,
    currentParentId,
    setCurrentParentId,
    filteredCategories,
    setEditingRequest,
    setMode,
  } = useRequestGallery();

  const deleteHandlers = useDeleteHandlers();

  const handleEditRequest = (e: React.MouseEvent, request: Request) => {
    e.stopPropagation();
    setEditingRequest(request);
    setMode("edit-request");
  };

  const onCategoryClick = (category: RequestCategory) => {
    if (!categories) return;
    const subCategories = categories.filter((c) => c.parent_id === category._id);
    if (subCategories.length > 0) {
      setCurrentParentId(category._id);
    } else {
      handleCategoryClick(category._id);
    }
  };

  const categoriesToShow = React.useMemo(
    () => getCategoriesToShow(searchQuery, filteredCategories, categories, currentParentId),
    [categories, currentParentId, searchQuery, filteredCategories]
  );

  if (isLoadingCategories) {
    return (
      <div className="flex flex-col items-center justify-center h-full p-6">
        <Loader className="animate-spin" />
        <span className="ml-2">Loading categories...</span>
      </div>
    );
  }

  return (
    <CategoryListContent
      onRequestSelect={onRequestSelect}
      categoriesToShow={categoriesToShow}
      onCategoryClick={onCategoryClick}
      handleEditRequest={handleEditRequest}
      deleteHandlers={deleteHandlers}
    />
  );
};

export default RequestCategoryList;
